import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'

const WEB_IDLE_TIMEOUT_MS = 15 * 60 * 1000
const WEB_AWAY_TIMEOUT_MS = 2 * 60 * 1000
const WARNING_WINDOW_MS = 60 * 1000

const PROTECTED_PATH_PREFIXES = [
    '/dashboard',
    '/notifications',
    '/send',
    '/tickets',
    '/wallet',
    '/transactions',
    '/profile',
    '/settings',
    '/donations',
    '/contributions',
    '/pay-bills',
    '/bank-transfers',
    '/payment',
    '/invoices',
    '/merchant',
    '/government',
]

const isProtectedPath = (pathname) =>
    PROTECTED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))

export default function IdleSessionGuard() {
    const location = useLocation()
    const { isAuthenticated, logout } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [warningActive, setWarningActive] = useState(false)
    const [secondsRemaining, setSecondsRemaining] = useState(
        Math.floor(WARNING_WINDOW_MS / 1000)
    )
    const warningTimerRef = useRef(null)
    const logoutTimerRef = useRef(null)
    const countdownTimerRef = useRef(null)
    const awayStartedAtRef = useRef(null)

    const monitoringEnabled = useMemo(
        () => isAuthenticated && isProtectedPath(location.pathname),
        [isAuthenticated, location.pathname]
    )

    const clearTimers = useCallback(() => {
        window.clearTimeout(warningTimerRef.current)
        window.clearTimeout(logoutTimerRef.current)
        window.clearInterval(countdownTimerRef.current)
        warningTimerRef.current = null
        logoutTimerRef.current = null
        countdownTimerRef.current = null
    }, [])

    const handleAutoLogout = useCallback(() => {
        clearTimers()
        setWarningActive(false)
        logout({ redirectTo: '/login' })
    }, [clearTimers, logout])

    const scheduleTimers = useCallback(() => {
        clearTimers()
        setWarningActive(false)
        setSecondsRemaining(Math.floor(WARNING_WINDOW_MS / 1000))

        warningTimerRef.current = window.setTimeout(() => {
            setWarningActive(true)
            setSecondsRemaining(Math.floor(WARNING_WINDOW_MS / 1000))
            countdownTimerRef.current = window.setInterval(() => {
                setSecondsRemaining((current) => (current > 0 ? current - 1 : 0))
            }, 1000)
        }, WEB_IDLE_TIMEOUT_MS - WARNING_WINDOW_MS)

        logoutTimerRef.current = window.setTimeout(handleAutoLogout, WEB_IDLE_TIMEOUT_MS)
    }, [clearTimers, handleAutoLogout])

    useEffect(() => {
        if (!monitoringEnabled) {
            clearTimers()
            setWarningActive(false)
            return undefined
        }

        const activityEvents = [
            'mousemove',
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
            'focus',
        ]
        const handleActivity = () => scheduleTimers()
        const handleVisibilityChange = () => {
            if (document.hidden) {
                awayStartedAtRef.current = Date.now()
                clearTimers()
                setWarningActive(false)
                return
            }

            const awayStartedAt = awayStartedAtRef.current
            awayStartedAtRef.current = null
            if (awayStartedAt && Date.now() - awayStartedAt >= WEB_AWAY_TIMEOUT_MS) {
                handleAutoLogout()
                return
            }

            scheduleTimers()
        }
        const handlePageHide = () => {
            awayStartedAtRef.current = Date.now()
            clearTimers()
            setWarningActive(false)
        }

        scheduleTimers()
        activityEvents.forEach((eventName) =>
            window.addEventListener(eventName, handleActivity, { passive: true })
        )
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pagehide', handlePageHide)

        return () => {
            activityEvents.forEach((eventName) =>
                window.removeEventListener(eventName, handleActivity)
            )
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('pagehide', handlePageHide)
            clearTimers()
        }
    }, [monitoringEnabled, scheduleTimers, clearTimers, handleAutoLogout])

    const warningOpen = monitoringEnabled && warningActive && secondsRemaining >= 0

    return (
        <Dialog
            open={warningOpen}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    background:
                        'linear-gradient(180deg, rgba(19,16,44,0.98), rgba(12,11,31,0.98))',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#fff',
                    minWidth: { xs: 'calc(100% - 32px)', sm: 420 },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>
                {isFr ? 'Session bientot terminee' : 'Session ending soon'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                    {isFr
                        ? 'MojoPayr deconnecte les sessions inactives apres 15 minutes. Votre session se terminera dans moins d une minute si vous ne continuez pas.'
                        : 'MojoPayr signs out inactive dashboard sessions after 15 minutes. Your session will end in under a minute unless you continue.'}
                </Typography>
                <Box
                    sx={{
                        mt: 2.5,
                        px: 2,
                        py: 1.6,
                        borderRadius: 3,
                        background: 'rgba(255,138,0,0.10)',
                        border: '1px solid rgba(255,138,0,0.18)',
                    }}
                >
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.66)' }}>
                        {isFr ? 'Temps restant' : 'Time remaining'}
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 900 }}>
                        {secondsRemaining}s
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={handleAutoLogout}
                    sx={{
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.14)',
                        textTransform: 'none',
                        fontWeight: 700,
                    }}
                    variant="outlined"
                >
                    {isFr ? 'Se deconnecter' : 'Sign out now'}
                </Button>
                <Button
                    onClick={scheduleTimers}
                    sx={{
                        background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                        color: '#0B0620',
                        textTransform: 'none',
                        fontWeight: 900,
                        '&:hover': { background: 'linear-gradient(90deg,#ff981f,#ffc068)' },
                    }}
                    variant="contained"
                >
                    {isFr ? 'Rester connecte' : 'Stay signed in'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
